import React, { useState } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardCard from './DashboardCard';
import { LinkButton } from '../styles';
import { SEVERITY, CARD_IDS } from '../constants';
import { formatDuration, formatNumber } from '../format';

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 9px 0;
  border-bottom: 1px solid #F1F4F8;
  font-size: 13px;

  &:last-child { border-bottom: none; }
`;

const Icon = styled.span`
  flex: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
`;

const Main = styled.div`
  flex: 1;
  min-width: 0;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Secondary = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: #6B7280;
`;

const PeopleList = styled.ul`
  margin: 6px 0 0;
  padding: 0;
  list-style: none;
  font-size: 12px;

  li {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 3px 0;
    color: ${({ theme }) => theme.colors.text.primary};
  }
  li span:last-child {
    color: #6B7280;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
`;

const Actions = styled.div`
  flex: none;
  display: flex;
  gap: 10px;
  align-items: center;
`;

// Uyarı türü → metin ve eylem. Eylem yetkiye göre: politika sayfası yalnız TA yazma, vardiya sayfası yalnız SM okuma.
// Hesaplama hatası ve eksik çıkış Eksik Kayıtlar'a gider (afiş aynı TimesheetCalcIssue kayıtlarını gösterir).
const TEXT = {
  calculationError: 'attendanceDashboard.warnings.calculationError',
  invalidOvertimePolicy: 'attendanceDashboard.warnings.invalidOvertimePolicy',
  overtimePolicyFrameworkMismatch: 'attendanceDashboard.warnings.overtimePolicyFrameworkMismatch',
  missingExit: 'attendanceDashboard.warnings.missingExit',
  pendingOvertime: 'attendanceDashboard.warnings.pendingOvertime',
  overtimeCap: 'attendanceDashboard.warnings.overtimeCap',
  noShiftNextWeek: 'attendanceDashboard.warnings.noShiftNextWeek',
};

const ACTION = {
  calculationError: { path: '/missing-records', label: 'attendanceDashboard.warnings.calculationErrorAction' },
  invalidOvertimePolicy: { path: '/overtime-policies', label: 'attendanceDashboard.warnings.invalidOvertimePolicyAction', needs: 'taWrite' },
  overtimePolicyFrameworkMismatch: { path: '/overtime-policies', label: 'attendanceDashboard.warnings.overtimePolicyFrameworkMismatchAction', needs: 'taWrite' },
  missingExit: { path: '/missing-records', label: 'attendanceDashboard.warnings.missingExitAction' },
  pendingOvertime: { path: '/overtime-approvals', label: 'attendanceDashboard.warnings.pendingOvertimeAction' },
  noShiftNextWeek: { path: '/shift-schedules', label: 'attendanceDashboard.warnings.noShiftNextWeekAction', needs: 'smRead' },
};

function warningText(item, t) {
  if (item.type === 'overtimeCap') {
    return item.capMinutes
      ? t(TEXT.overtimeCap, { count: item.count, cap: formatDuration(item.capMinutes, t) })
      : t('attendanceDashboard.warnings.overtimeCapMixed', { count: item.count });
  }
  if (item.type === 'pendingOvertime') {
    return t(TEXT.pendingOvertime, { count: item.count, duration: formatDuration(item.minutes ?? 0, t) });
  }
  return TEXT[item.type] ? t(TEXT[item.type], { count: item.count }) : null;
}

// Eylem yolu: eksik çıkış bağlantısı Eksik Kayıtlar'ı uyarının saydığı aralık ve "missing_exit" koduyla açar (I4).
function actionPath(item, action) {
  if (item.type === 'missingExit' && item.rangeFrom && item.rangeTo) {
    return `${action.path}?from=${item.rangeFrom}&to=${item.rangeTo}&code=missing_exit`;
  }
  return action.path;
}

// İkincil satır: en fazla 10 ad; kalanlar "+N daha" (inceleme M8). Adı olmayan kişi (çalışan kaydı yok) e-postasıyla
// DEĞİL "Bilinmeyen Kişi" ile yazılır.
function secondaryText(item, t) {
  if (item.type === 'invalidOvertimePolicy') {
    const names = (item.policies || []).map((p) => p.name).filter(Boolean);
    const rest = (item.count || 0) - names.length;
    return [names.join(', '), rest > 0 ? t('attendanceDashboard.warnings.morePolicies', { count: rest }) : null]
      .filter(Boolean).join(' ');
  }
  if (item.type === 'overtimePolicyFrameworkMismatch') {
    const people = item.people || [];
    const names = people.map((p) => p.fullName || t('attendanceDashboard.unknownPerson'));
    const rest = (item.count || 0) - people.length;
    return [names.join(', '), rest > 0 ? t('attendanceDashboard.warnings.morePeople', { count: rest }) : null]
      .filter(Boolean).join(' ');
  }
  return '';
}

// Uyarılar: tür sırası backend'den. Renk tek başına anlam taşımaz — ikon dairesinde "!" / "i" + kalın sayı metni.
export default function WarningsCard({ slice, canWriteTa, canReadSm }) {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const lang = i18n.language;
  const [capOpen, setCapOpen] = useState(false);
  const items = (slice.data?.items || []).filter((item) => TEXT[item.type]);

  const allowed = (action) => {
    if (!action) return false;
    if (action.needs === 'taWrite') return Boolean(canWriteTa);
    if (action.needs === 'smRead') return Boolean(canReadSm);
    return true;
  };

  return (
    <DashboardCard
      cardKey="uyarilar"
      title={t('attendanceDashboard.warnings.title')}
      span={6}
      spanMd={6}
      slice={slice}
      empty={items.length === 0}
      emptyText={t('attendanceDashboard.warnings.empty')}
      domId={CARD_IDS.warnings}
    >
      <div>
        {items.map((item) => {
          const severity = SEVERITY[item.severity] || SEVERITY.info;
          const action = ACTION[item.type];
          const secondary = secondaryText(item, t);
          const capRest = item.type === 'overtimeCap' ? (item.count || 0) - (item.people || []).length : 0;
          return (
            <Row key={item.type} data-warning={item.type}>
              <Icon $bg={severity.bg} $fg={severity.fg} aria-hidden>{item.severity === 'info' ? 'i' : '!'}</Icon>
              <Main>
                <strong>{warningText(item, t)}</strong>
                {secondary ? <Secondary>{secondary}</Secondary> : null}
                {item.type === 'overtimeCap' && capOpen ? (
                  <PeopleList data-cap-list>
                    {(item.people || []).map((person) => (
                      <li key={person.userId}>
                        <span>{person.fullName || t('attendanceDashboard.unknownPerson')}</span>
                        <span>
                          {t('attendanceDashboard.warnings.capUsage', {
                            used: formatDuration(person.usedMinutes ?? 0, t),
                            cap: person.capMinutes ? formatDuration(person.capMinutes, t) : formatNumber(0, lang),
                          })}
                        </span>
                      </li>
                    ))}
                    {capRest > 0 ? (
                      <li data-more>
                        <span>{t('attendanceDashboard.warnings.morePeople', { count: capRest })}</span>
                        <span />
                      </li>
                    ) : null}
                  </PeopleList>
                ) : null}
              </Main>
              <Actions data-export-exclude>
                {item.type === 'overtimeCap' ? (
                  <LinkButton type="button" data-action="toggle-cap" onClick={() => setCapOpen((v) => !v)}>
                    {capOpen ? t('attendanceDashboard.warnings.overtimeCapHide') : t('attendanceDashboard.warnings.overtimeCapShow')}
                  </LinkButton>
                ) : null}
                {allowed(action) ? (
                  <LinkButton type="button" data-action="open" onClick={() => history.push(actionPath(item, action))}>
                    {t(action.label)}
                  </LinkButton>
                ) : null}
              </Actions>
            </Row>
          );
        })}
      </div>
    </DashboardCard>
  );
}
